/**
 * 1920×1080 슬라이드 캔버스. master-of-slide 의 slide-canvas.tsx 패턴 미러.
 *
 * - outer container: relative, 부모 크기 채움 (overflow-hidden)
 * - inner box: scaledW × scaledH (transform 적용 후 실제 visible 크기), absolute center
 * - innermost: 1920×1080 고정 + transform: scale(s), transform-origin top-left → 정확한 px 보존
 *
 * design tokens 는 innermost 에 CSS 변수로 노출 (--osd-*) 해서 자식 트리가 사용.
 */
import type { SlideDesign } from '@innermate/shared';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

interface Props {
  design: SlideDesign;
  children: ReactNode;
  /** 강제 scale 값 (썸네일 등). 미지정 시 부모 크기에 맞춰 contain. */
  scale?: number;
  /** 부모 컨테이너 안 중앙 정렬 (기본 true). */
  center?: boolean;
}

const CANVAS_W = 1920;
const CANVAS_H = 1080;

export function SlideCanvas({ design, children, scale, center = true }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fitScale, setFitScale] = useState(1);

  useEffect(() => {
    if (scale !== undefined) return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      setFitScale(Math.min(width / CANVAS_W, height / CANVAS_H));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale]);

  const s = scale ?? fitScale;
  const scaledW = CANVAS_W * s;
  const scaledH = CANVAS_H * s;

  const cssVars: Record<string, string> = {
    '--osd-bg': design.palette.bg,
    '--osd-text': design.palette.text,
    '--osd-accent': design.palette.accent,
    '--osd-font-display': design.fonts.display,
    '--osd-font-body': design.fonts.body,
    '--osd-size-hero': `${design.typeScale.hero}px`,
    '--osd-size-body': `${design.typeScale.body}px`,
    '--osd-radius': `${design.radius}px`,
  };

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden">
      <div
        className="overflow-hidden"
        style={{
          width: scaledW,
          height: scaledH,
          background: design.palette.bg,
          ...(center
            ? {
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }
            : {}),
        }}
      >
        <div
          style={
            {
              width: CANVAS_W,
              height: CANVAS_H,
              transform: `scale(${s})`,
              transformOrigin: 'top left',
              color: design.palette.text,
              fontFamily: design.fonts.body,
              position: 'relative',
              ...cssVars,
            } as CSSProperties
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export const SLIDE_CANVAS_W = CANVAS_W;
export const SLIDE_CANVAS_H = CANVAS_H;
